import { Fragment, ReactNode } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldGroupHeader, FieldsTable } from '@/components/layout/field-row'
import { Building } from '@/reducers/types'

import {
  PlaceFeatureDefinition,
  PlaceFeatureGroup,
  PlaceFeatureKey,
  PlaceFlags,
} from '../../features/places-features.constants'
import { getFeatureChildren, getFeaturesByGroup } from '../../features/places-features.rules'
import { PlaceFeatureRow } from './place-feature-row'
import { PlaceFeatureRowSkeleton } from './place-feature-row-skeleton'

// Note: short card titles; the first row of the card already carries the full "<provider> available" label
const PROVIDER_TITLES = {
  brivoAvailable: 'brivo',
  butterflyAvailable: 'butterfly',
  iglooWorkAvailable: 'iglooWork',
  pointCentralAvailable: 'pointCentral',
  remoteLockAvailable: 'remoteLock',
  saltoSpaceAvailable: 'saltoSpace',
  swiftlaneAvailable: 'swiftlane',
} as const

const GroupCount = ({ keys }: { keys: PlaceFeatureKey[] }) => {
  const { t } = useTranslation(['organisation'])
  const values = useWatch<PlaceFlags>()
  const enabled = keys.filter((key) => values[key]).length

  return (
    <span>
      {t('organisation:places.features.tab.enabledCount', { enabled, total: keys.length })}
    </span>
  )
}

interface PlaceFeatureGroupProps {
  // Note: without a building (still loading) every row shows a skeleton value
  building?: Building
  customLabels?: Partial<Record<PlaceFeatureKey, string>>
  errorFeatures?: string[]
  errorMessage?: string
  group: PlaceFeatureGroup
  isAdmin: boolean
  isEditing: boolean
  // Note: shows "enabled of total" for the group in its header
  showCount?: boolean
  // Note: renders every provider branch of a single-root group as its own category card
  splitByBranch?: boolean
  // Note: with splitByBranch, renders only the root card or only the provider cards
  splitPart?: 'branches' | 'root'
  // Note: with splitPart 'branches', cards of other groups sorted (biggest first, then by title)
  // together with the provider cards
  extraCards?: PlaceFeatureExtraCard[]
}

export interface PlaceFeatureExtraCard {
  key: string
  node: ReactNode
  size: number
  title: string
}

export const PlaceFeatureGroupSection = ({
  building,
  customLabels,
  errorFeatures = [],
  errorMessage,
  group,
  isAdmin,
  isEditing,
  showCount,
  splitByBranch,
  splitPart,
  extraCards = [],
}: PlaceFeatureGroupProps) => {
  const { t } = useTranslation(['organisation'])
  const features = getFeaturesByGroup(group).filter(
    (feature: PlaceFeatureDefinition) => !feature.hidden,
  )
  const roots = features.filter(
    (feature) => !feature.parent || !features.some(({ key }) => key === feature.parent),
  )

  const renderNode = (feature: PlaceFeatureDefinition, depth: number): React.ReactNode[] => {
    const children = getFeatureChildren(feature.key).filter(
      (child: PlaceFeatureDefinition) =>
        !child.hidden && features.some(({ key }) => key === child.key),
    )

    return [
      building ? (
        <PlaceFeatureRow
          key={feature.key}
          building={building}
          customLabel={customLabels?.[feature.key as PlaceFeatureKey]}
          depth={depth}
          error={errorFeatures.includes(feature.key) ? errorMessage : undefined}
          featureKey={feature.key as PlaceFeatureKey}
          isAdmin={isAdmin}
          isEditing={isEditing}
        />
      ) : (
        <PlaceFeatureRowSkeleton
          key={feature.key}
          customLabel={customLabels?.[feature.key as PlaceFeatureKey]}
          depth={depth}
          featureKey={feature.key as PlaceFeatureKey}
        />
      ),
      ...children.flatMap((child) => renderNode(child, depth + 1)),
    ]
  }

  const getChildren = (feature: PlaceFeatureDefinition) =>
    getFeatureChildren(feature.key).filter(
      (child: PlaceFeatureDefinition) =>
        !child.hidden && features.some(({ key }) => key === child.key),
    )

  const collectKeys = (feature: PlaceFeatureDefinition): PlaceFeatureKey[] => [
    feature.key as PlaceFeatureKey,
    ...getChildren(feature).flatMap(collectKeys),
  ]

  const getBranchTitle = (key: PlaceFeatureKey) => {
    const provider = PROVIDER_TITLES[key as keyof typeof PROVIDER_TITLES]
    return provider
      ? t(`organisation:places.accessControl.providers.${provider}`)
      : t(`organisation:places.building.toggles.${key}`)
  }

  const groupTitle = t(`organisation:places.features.groups.${group}`)
  const [root] = roots

  if (splitByBranch && roots.length === 1) {
    const kids = getChildren(root)
    const branches = kids.filter((kid) => getChildren(kid).length > 0)
    const leaves = kids.filter((kid) => getChildren(kid).length === 0)

    if (splitPart === 'branches') {
      const cards: PlaceFeatureExtraCard[] = [
        ...branches.map((branch) => {
          const title = getBranchTitle(branch.key as PlaceFeatureKey)
          return {
            key: branch.key,
            node: (
              <FieldsTable>
                <FieldGroupHeader>
                  {title}
                  {showCount && <GroupCount keys={collectKeys(branch)} />}
                </FieldGroupHeader>
                {renderNode(branch, 0)}
              </FieldsTable>
            ),
            size: collectKeys(branch).length,
            title,
          }
        }),
        ...extraCards,
      ].sort((a, b) => b.size - a.size || a.title.localeCompare(b.title))

      return cards.map(({ key, node }) => <Fragment key={key}>{node}</Fragment>)
    }

    return (
      <>
        <FieldsTable>
          <FieldGroupHeader>
            {groupTitle}
            {showCount && (
              <GroupCount
                keys={[root.key as PlaceFeatureKey, ...leaves.flatMap((leaf) => collectKeys(leaf))]}
              />
            )}
          </FieldGroupHeader>
          {[renderNode(root, 0)[0], ...leaves.flatMap((leaf) => renderNode(leaf, 1))]}
        </FieldsTable>
        {splitPart !== 'root' &&
          branches.map((branch) => (
            <FieldsTable key={branch.key}>
              <FieldGroupHeader>
                {getBranchTitle(branch.key as PlaceFeatureKey)}
                {showCount && <GroupCount keys={collectKeys(branch)} />}
              </FieldGroupHeader>
              {renderNode(branch, 0)}
            </FieldsTable>
          ))}
      </>
    )
  }

  const rendered = roots.flatMap((feature) => renderNode(feature, 0))
  if (!rendered.length) return null

  return (
    <FieldsTable>
      <FieldGroupHeader>
        {groupTitle}
        {showCount && <GroupCount keys={features.map(({ key }) => key as PlaceFeatureKey)} />}
      </FieldGroupHeader>
      {rendered}
    </FieldsTable>
  )
}
